import useLogAmplitudeEventOnce from '@eversports/amplitude-react/useLogAmplitudeEventOnce'
import FilledHorizontalLogo from '@eversports/design-tokens/assets/logo/filled-horizontal.svg'
import EversportsLogoIcon from '@eversports/design-tokens/assets/logo/icon.svg'
import Avatar from '@eversports/klimt-primitives/legacy/Avatar'
import Box from '@eversports/klimt-primitives/legacy/Box'
import Heading from '@eversports/klimt-primitives/legacy/Heading'
import Stack from '@eversports/klimt-primitives/legacy/Stack'
import Text from '@eversports/klimt-primitives/legacy/Text'
import { motion } from '@eversports/klimt-utilities/framer-motion'
import { useSettingsContext } from '@eversports/react-app-base/SettingsContext'
import { useEffect, useState } from 'react'
import ReactConfetti from 'react-confetti'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'

import { RECAP_YEAR } from '../../App.constants'
import LoadingPage from '../../components/LoadingPage'
import MetaHelmet from '../../components/MetaHelmet'
import { useYearlyRecapRecapQuery } from '../../graphql'
import getStaticPageMetadata from '../../helpers/get-static-page-metadata'
import useUrlParams from '../../hooks/use-url-params'
import { Localized, useIntl } from '../../localization/react'
import BricolageGrotesqueFont from '../Home/assets/bricolage-grotesque-font-face'

import AnimatedTitle from './AnimatedTitle'
import ActiveDaysVideo from './assets/active-days-video.mp4'
import ActiveMinutesVideo from './assets/active-minutes-video.mp4'
import BackgroundPatternImage from './assets/background-pattern-image.webp'
import DefaultUserPicture from './assets/default-user-picture.webp'
import MostActiveMonthVideo from './assets/most-active-month-video.mp4'
import NumberOfSportsVideo from './assets/number-of-sports-video.mp4'
import StreakVideo from './assets/streak-video.mp4'
import TopFriendsVideo from './assets/top-friends-video.mp4'
import TopSportVideo from './assets/top-sport-video.mp4'
import TopTrainersVideo from './assets/top-teachers-video.mp4'
import TopVenuesVideo from './assets/top-venues-video.mp4'
import findMostActiveMonth from './helpers/find-most-active-month'
import getRecapSectionSubtitles from './helpers/get-recap-section-subtitles'
import RecapOverview from './RecapOverview'
import RecapSection from './RecapSection'
import { RecapSectionImage } from './RecapSectionImage'

const YearlyRecap = () => {
  const intl = useIntl()
  const { view } = useUrlParams()
  const { amplitude, logAmplitudeEventOnce } = useLogAmplitudeEventOnce()
  const { origin } = useSettingsContext()
  const { recapToken } = useParams<{ recapToken: string }>()
  const { data, loading } = useYearlyRecapRecapQuery({ variables: { token: recapToken } })
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    if (!amplitude || !data) return
    logAmplitudeEventOnce({
      eventName: `Viewed ${RECAP_YEAR} Yearly Recap`,
      eventProperties: {
        userType: view ? 'visitor' : 'owner',
      },
    })
  }, [amplitude, data])

  useEffect(() => {
    setWindowSize({ width: window.innerWidth, height: window.innerHeight })
  }, [])

  if (!data || loading) {
    return <LoadingPage />
  }

  const recapUrl = `${origin}/user/${recapToken}/yearly-recap`

  const meta = getStaticPageMetadata({
    title: intl.yearlyRecapPageMetaTitle({ firstName: data.recap.user.firstName }),
    description: intl.yearlyRecapPageMetaDescription({ firstName: data.recap.user.firstName }),
    image: data.recap.user.profilePicture,
    url: recapUrl,
  })

  const topFriends = data.recap.friends.slice(0, 5)
  const topTrainers = data.recap.trainers.slice(0, 5)

  const friendsListItems = topFriends.map((friend) => {
    return {
      name: friend.friend.publicUser.firstName,
      count: friend.numberOfCommonActivities,
      imageSrc: friend.friend.publicUser.profilePicture
        ? friend.friend.publicUser.profilePicture.medium
        : DefaultUserPicture,
    }
  })

  const trainersListItems = topTrainers.map((trainer) => {
    return {
      name: trainer.trainer.venues.length
        ? `${trainer.trainer.nickname} - ${trainer.trainer.venues[0].name}`
        : trainer.trainer.nickname,
      count: trainer.activityCount,
      imageSrc: trainer.trainer.image ? trainer.trainer.image.medium : DefaultUserPicture,
    }
  })

  const venuesListItems = data.recap.venues.map((venue) => {
    return {
      name: venue.venue.name,
      count: venue.activityCount,
      imageSrc: venue.venue.logo ? venue.venue.logo?.medium : EversportsLogoIcon,
    }
  })

  const sportsListItems = data.recap.sports.map((sport) => {
    return {
      name: sport.sport.name,
      count: sport.activityCount,
    }
  })

  const mostActiveMonth = findMostActiveMonth(data.recap)
  const {
    favoriteVenueText,
    minutesActiveText,
    activeDaysText,
    mostActiveMonthText,
    sportsCountText,
    topSportText,
    topTrainerText,
    topFriendText,
    streakText,
  } = getRecapSectionSubtitles({ recap: data.recap, intl })

  return (
    <>
      <Helmet>
        <style>{BricolageGrotesqueFont}</style>
      </Helmet>
      <MetaHelmet meta={meta} noIndex />
      {windowSize.width > 0 && (
        <ReactConfetti
          width={windowSize.width}
          height={windowSize.height}
          style={{ pointerEvents: 'none' }}
          numberOfPieces={windowSize.width > 600 ? 1000 : 500}
          recycle={false}
          onConfettiComplete={(confetti) => {
            confetti?.reset()
          }}
        />
      )}
      <Box
        width="100%"
        sx={{
          position: 'relative',
          backgroundColor: '#F3F8F4',
          height: '100dvh',
          overflowY: 'scroll',
          scrollSnapType: 'y mandatory',
          '&::before': {
            content: '""',
            position: 'absolute',
            backgroundImage: `url(${BackgroundPatternImage})`,
            backgroundSize: 'cover',
            backgroundPosition: { xs: 'center', lg: 'initial' },
            opacity: 0.4,
          },
        }}
      >
        <Stack
          alignItems="center"
          sx={{
            maxWidth: { xs: '100%', md: '500px' },
            position: 'relative',
            zIndex: 1,
          }}
          margin="auto"
        >
          <Stack
            justifyContent="center"
            padding={8}
            width="100%"
            minHeight="100dvh"
            gap="4vh"
            sx={{ scrollSnapAlign: 'start' }}
          >
            <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <Box alignItems="center" justifyItems="center">
                <img width="295px" height="60px" src={FilledHorizontalLogo} />
              </Box>
            </motion.div>
            <Stack gap={4} alignItems="center">
              <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                <Stack gap={1} width="100%">
                  <Heading
                    is="h1"
                    sx={{
                      color: '#194148',
                      fontFamily: "'Bricolage Grotesque', 'Adjusted Bricolage Grotesque Fallback'",
                      fontWeight: 300,
                      lineHeight: { xs: '36px', sm: '44px' },
                      fontSize: { xs: '24px', sm: '32px' },
                    }}
                  >
                    <Localized id="yearly-recap-title" params={{ firstName: data.recap.user.firstName }} />
                    <Text
                      sx={{
                        color: '#194148',
                        fontFamily: "'Bricolage Grotesque', 'Adjusted Bricolage Grotesque Fallback'",
                        fontSize: '36px',
                        fontWeight: '700',
                        lineHeight: '44px',
                      }}
                    >
                      {data.recap.user.firstName}
                    </Text>
                  </Heading>
                </Stack>
              </motion.div>
            </Stack>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 260, damping: 20 }}
            >
              <Avatar
                src={data.recap.user.profilePicture?.medium ?? DefaultUserPicture}
                sx={{
                  margin: 'auto',
                  width: { xs: 200, sm: 240 },
                  height: { xs: 200, sm: 240 },
                  backgroundColor: 'gray',
                  boxShadow: '0 40px 80px 0 rgba(0, 67, 73, 0.80)',
                }}
              />
            </motion.div>
            <Stack gap={6} marginTop={4}>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.5 }}>
                <Stack gap={4} alignItems="right">
                  <Heading
                    is="h2"
                    sx={{
                      letterSpacing: '16px',
                      textAlign: 'right',
                      color: '#194148',
                      fontSize: { xs: '28px', sm: '57px' },
                      lineHeight: { xs: '32px', sm: '64px' },
                      fontFamily: "'Bricolage Grotesque', 'Adjusted Bricolage Grotesque Fallback'",
                      fontWeight: '200',
                      textTransform: 'uppercase',
                    }}
                  >
                    <Localized id="yearly-recap-your-year" />
                  </Heading>
                  <Text
                    sx={{
                      width: '100%',
                      textAlign: 'right',
                      fontFamily: "'Bricolage Grotesque', 'Adjusted Bricolage Grotesque Fallback'",
                      lineHeight: '64px',
                      color: '#194148',
                      fontSize: '94px',
                      fontWeight: '700',
                      letterSpacing: '10px',
                    }}
                  >
                    {data.recap.year}
                  </Text>
                </Stack>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: 1.5,
                  duration: 0.5,
                  repeatType: 'reverse',
                  repeatDelay: 0.5,
                }}
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Box
                  sx={{
                    width: '32px',
                    height: '32px',
                    color: '#194148',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 384 512"
                    width="100%"
                    height="100%"
                    aria-label="Scroll down"
                  >
                    <path
                      fill="#194148"
                      d="M175.5 505c9.4 9.4 24.6 9.4 33.9 0l168-168c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-127 127 0-406.1c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 406.1-127-127c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l168 168z"
                    />
                  </svg>
                </Box>
              </motion.div>
            </Stack>
          </Stack>
          <RecapSection
            title={data.recap.sports[0].sport.name}
            subtitle={topSportText}
            list={{ type: 'sport', items: sportsListItems }}
            backgroundVideo={TopSportVideo}
          />
          <RecapSection
            title={
              <Localized id="yearly-recap-sports-section-title" params={{ totalSportsCount: data.recap.sportsCount }} />
            }
            subtitle={sportsCountText}
            backgroundVideo={NumberOfSportsVideo}
          />
          <RecapSection
            title={
              <AnimatedTitle
                value={data.recap.activeDays}
                title={<Localized id="yearly-recap-active-days-section-title-text" />}
                variant="value-first"
              />
            }
            subtitle={activeDaysText}
            backgroundVideo={ActiveDaysVideo}
          />
          <RecapSection
            title={
              <AnimatedTitle
                value={data.recap.minutesActive}
                title={<Localized id="yearly-recap-active-minutes-section-title-text" />}
                variant="value-first"
              />
            }
            subtitle={minutesActiveText}
            backgroundVideo={ActiveMinutesVideo}
          />
          <RecapSection
            title={
              <Localized
                id="yearly-recap-most-active-month-section-title"
                params={{ monthName: <Localized id="month-of-the-year" params={{ month: mostActiveMonth?.month }} /> }}
              />
            }
            subtitle={mostActiveMonthText}
            backgroundVideo={MostActiveMonthVideo}
          />
          {data.recap.longestStreak.days >= 3 && (
            <RecapSection
              title={
                <AnimatedTitle
                  value={data.recap.longestStreak.days}
                  title={<Localized id="yearly-recap-streak-section-title-text" />}
                  variant="title-first"
                />
              }
              subtitle={streakText}
              backgroundVideo={StreakVideo}
            />
          )}
          <RecapSection
            title={venuesListItems[0].name}
            subtitle={favoriteVenueText}
            image={<RecapSectionImage type="venue" src={venuesListItems[0].imageSrc} variant="large" />}
            list={{ type: 'venue', items: venuesListItems }}
            backgroundVideo={TopVenuesVideo}
          />
          {topTrainers.length >= 3 && (
            <RecapSection
              title={data.recap.trainers[0].trainer.nickname}
              subtitle={topTrainerText}
              image={<RecapSectionImage type="person" src={trainersListItems[0].imageSrc} variant="large" />}
              list={{ type: 'person', items: trainersListItems }}
              backgroundVideo={TopTrainersVideo}
            />
          )}
          {topFriends.length >= 3 && (
            <RecapSection
              title={friendsListItems[0].name}
              subtitle={topFriendText}
              image={<RecapSectionImage type="person" src={friendsListItems[0].imageSrc} variant="large" />}
              list={{ type: 'person', items: friendsListItems }}
              backgroundVideo={TopFriendsVideo}
            />
          )}
          <RecapOverview recap={data.recap} />
        </Stack>
      </Box>
    </>
  )
}

export default YearlyRecap
